import React from "react";
import {
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Chip,
  Divider,
  CardContent,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import SchoolIcon from "@mui/icons-material/School";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import GroupsIcon from "@mui/icons-material/Groups";

import { StyledCard } from "../common/StyledCard";
import InfoRow from "../common/InfoRow";
import { ClassroomResponseDto } from "../../shared/dtos/classroom/ClassroomResponseDto";

interface ClassroomCardProps {
  classroom: ClassroomResponseDto;
  onEdit?: () => void;
  onDelete?: () => void;
}

const ClassroomCard: React.FC<ClassroomCardProps> = ({
  classroom,
  onEdit,
  onDelete,
}) => {
  return (
    <StyledCard>
      <CardContent
        sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}
      >
        {/* Header */}
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {classroom.name}
            </Typography>
            {classroom.gradeLevelName && (
              <Chip
                label={classroom.gradeLevelName}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ mt: 0.5, height: 20, fontSize: "0.7rem" }}
              />
            )}
          </Box>
          <Stack direction="row">
            {onEdit && (
              <Tooltip title="Edit Classroom">
                <IconButton size="small" color="secondary" onClick={onEdit}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete Classroom">
                <IconButton size="small" color="error" onClick={onDelete}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Details Body */}
        <Stack spacing={1.5} sx={{ flexGrow: 1 }}>
          {classroom.branchName && (
            <InfoRow
              icon={<SchoolIcon fontSize="small" />}
              label="Branch"
              value={classroom.branchName}
            />
          )}
          <InfoRow
            icon={<MeetingRoomIcon fontSize="small" />}
            label="Location"
            value={classroom.location}
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1.5,
            }}
          >
            <InfoRow
              icon={<GroupsIcon fontSize="small" />}
              label="Capacity"
              value={classroom.capacity}
            />
            {classroom.academicYearName && (
              <InfoRow
                icon={<CalendarTodayIcon fontSize="small" />}
                label="Academic Year"
                value={classroom.academicYearName}
              />
            )}
          </Box>
        </Stack>

        {/* Homeroom Teacher Footer */}
        {classroom.homeroomTeacherName && (
          <Box
            sx={{
              mt: 2,
              pt: 1,
              borderTop: "1px dashed",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block" }}
            >
              Homeroom Teacher: <b>{classroom.homeroomTeacherName}</b>
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default ClassroomCard;
